<script lang="ts" setup>
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import { usePushStore } from '../../stores/push'

const $q = useQuasar()
const store = usePushStore()
const { enabled, configLoaded, subscribed, support, busy } = storeToRefs(store)

async function run(action: () => ReturnType<typeof store.enable>) {
  const result = await action()
  if (result.ok) return
  $q.notify({ color: 'negative', textColor: 'white', icon: 'mdi-alert', message: result.message })
}

onMounted(async () => {
  await store.loadConfig()
  await store.refreshDevice()
})
</script>

<template>
  <!-- Hidden until the server says push is configured. Nothing here asks the browser for permission; only Turn on does. -->
  <q-card
    v-if="configLoaded && enabled"
    flat
    bordered
    id="push-notifications"
    class="settings-card"
    data-testid="push-card"
  >
    <q-card-section>
      <div class="text-h6">Push notifications</div>
    </q-card-section>

    <q-card-section v-if="support === 'ios-needs-install'" data-testid="push-ios-install">
      <div>On iPhone and iPad, push works only in the installed app (iOS 16.4 or later).</div>
      <ol class="q-mt-sm q-mb-none q-pl-md">
        <li>Tap Share</li>
        <li>Add to Home Screen</li>
        <li>Open RoadFootage from Home Screen</li>
      </ol>
    </q-card-section>

    <q-card-section v-else-if="support === 'unsupported'" data-testid="push-unsupported">
      This browser can't receive push notifications.
    </q-card-section>

    <q-card-section v-else-if="support === 'denied'" data-testid="push-blocked">
      Notifications are blocked for this site. Allow them in your browser settings.
    </q-card-section>

    <q-card-section v-else-if="subscribed" class="column q-gutter-sm" data-testid="push-on">
      <div class="row items-center no-wrap justify-between q-gutter-sm">
        <div>On for this device</div>
        <q-btn
          outline
          label="Turn off"
          :loading="busy"
          data-testid="push-turn-off"
          @click="run(() => store.disable())"
        />
      </div>
      <div class="text-caption text-grey-7">Uses your in-app notification settings.</div>
    </q-card-section>

    <q-card-section v-else class="column items-start q-gutter-sm" data-testid="push-off">
      <div class="text-grey-8">Get updates on this device, even when RoadFootage is closed.</div>
      <div>Off on this device</div>
      <q-btn
        color="primary"
        label="Turn on"
        :loading="busy"
        data-testid="push-turn-on"
        @click="run(() => store.enable())"
      />
    </q-card-section>
  </q-card>
</template>
